import { createFileRoute } from '@tanstack/react-router'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { BanknoteArrowDownIcon, DollarSignIcon, WalletIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { Withdrawal } from '@/tygo/db'
import { useBalance } from '@/domains/balance/hooks/useBalance'
import {
  useGetWithdrawalsQuery,
  useWithdrawMutation,
} from '@/domains/balance/queries'
import { USDCToString, dollarsToMicroUSDC } from '@/lib/currency'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { CopyButton } from '@/components/CopyButton'
import { useUserQuery } from '@/domains/user/queries'
import { useAppForm } from '@/lib/form'
import { WithdrawForm } from '@/domains/balance/form/withdraw-form'
import { Button } from '@/components/ui/button'

const columnHelper = createColumnHelper<Withdrawal>()

const EMPTY_WITHDRAWALS: Array<Withdrawal> = []

export const Route = createFileRoute('/account/wallet')({
  component: RouteComponent,
})

function RouteComponent() {
  const balance = useBalance()
  const { data: user } = useUserQuery()
  const { mutateAsync: withdraw } = useWithdrawMutation()
  const { data: withdrawals } = useGetWithdrawalsQuery()
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false)

  const columns = useMemo(
    () => [
      columnHelper.accessor('amount', {
        header: 'Amount',
        cell: ({ row }) => <div>${USDCToString(row.original.amount)}</div>,
      }),
      columnHelper.accessor('wallet_address', {
        header: 'Address',
        cell: ({ row }) => (
          <div className="font-mono text-xs">
            {row.original.wallet_address.slice(0, 6)}...
            {row.original.wallet_address.slice(-4)}
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ row }) => (
          <div className="capitalize">{row.original.status}</div>
        ),
      }),
    ],
    [],
  )

  const table = useReactTable({
    data: withdrawals ?? EMPTY_WITHDRAWALS,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const form = useAppForm({
    defaultValues: {
      walletAddress: '',
      amount: '',
    },
    onSubmit: async ({ value }) => {
      const amountMicro = dollarsToMicroUSDC(parseFloat(value.amount))
      try {
        await withdraw({
          walletAddress: value.walletAddress,
          amount: amountMicro,
        })
        setWithdrawDialogOpen(false)
        toast.success('Withdrawal initiated')
      } catch {
        toast.error('Failed to initiate withdrawal')
      }
    },
  })

  return (
    <div className="flex flex-col gap-6 px-4 max-w-5xl mx-auto w-full">
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-medium tracking-tight">Wallet</h2>
        <p className="text-muted-foreground">
          Manage your balance and deposit funds securely.
        </p>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <div className="flex flex-col gap-1">
              <CardTitle className="text-sm font-medium">
                Total Balance
              </CardTitle>
              <CardDescription>
                Available for gameplay and withdrawal
              </CardDescription>
            </div>
            <DollarSignIcon className="h-4 w-4 text-muted-foreground mt-1" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-medium">
              ${USDCToString(balance ?? 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
            <div className="flex flex-col gap-1">
              <CardTitle className="text-sm font-medium">
                Deposit Address (USDC on Base)
              </CardTitle>
              <CardDescription>
                Only send USDC on the Base network to this address.
              </CardDescription>
            </div>
            <WalletIcon className="h-4 w-4 text-muted-foreground mt-1" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1">
              <p className="text-xs font-mono bg-muted p-3 rounded-md border break-all">
                {user?.deposit_address || 'Generating address...'}
              </p>
            </div>
            {user?.deposit_address && (
              <div className="flex justify-end">
                <CopyButton text={user.deposit_address} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-medium">Withdrawal History</h3>
          <WithdrawForm
            form={form}
            balance={balance ?? 0}
            open={withdrawDialogOpen}
            onOpenChange={setWithdrawDialogOpen}
            trigger={
              <Button disabled={!balance || balance === 0}>
                <BanknoteArrowDownIcon />
                Withdraw
              </Button>
            }
          />
        </div>
        <Table className="mt-4">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="cursor-default">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="cursor-default">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No withdrawals yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
